import { fetchFragmentArea } from '@gialaboratory/sdk/report-check-fragments';
import { useQuery } from '@tanstack/react-query';
import React, { useContext, useEffect } from 'react';
import { FragmentRenderer } from '@gialaboratory/sdk/fragment-renderer';
import LandingSkeleton from '@@components/Landing/LandingSkeleton';
import LookupForm from '@@components/LookupForm/LookupForm';
import { TLocale } from '@@config/locale/';
import { LocaleContext, TLocaleContext } from '@@contexts/LocaleContext';
import { getMagnoliaRestBaseURL } from '@@utils/utils';

const FRAGMENT_PAGE_NAME = 'landing' as const;

const fetchLandingContent = async (locale: TLocale) => {
    const baseUrl = getMagnoliaRestBaseURL();
    try {
        return await fetchFragmentArea(FRAGMENT_PAGE_NAME, locale, { baseUrl });
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(
            '[Landing] Fragment fetch failed:',
            msg,
            'url:',
            `${baseUrl}/.rest/api/report-check/fragments/${FRAGMENT_PAGE_NAME}/${locale}`
        );
        throw err;
    }
};

const Landing: React.FC = () => {
    const { locale }: TLocaleContext = useContext(LocaleContext);

    const reportNo = new URLSearchParams(window.location.search).get('reportno') ?? '';

    const {
        data: content,
        isPending,
        error,
    } = useQuery({
        queryKey: ['reportCheckFragment', FRAGMENT_PAGE_NAME, locale],
        queryFn: () => fetchLandingContent(locale),
        staleTime: 15 * 60 * 1000, // 15 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
    });

    useEffect(() => {
        const checkStorageSize = () => {
            const size = localStorage.getItem('report-check-cache')?.length ?? 0;
            const kb = size / 1024;
            const mb = kb / 1024;

            console.info(`report-check-cache size: ${kb.toFixed(2)} KB (${mb.toFixed(4)} MB)`);
        };
        checkStorageSize();
        const interval = setInterval(checkStorageSize, 24 * 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const isLookupFormColumn = (nodeId: string, node: Record<string, unknown>) => {
        const idFromNode = node['id'] ?? node['@id'];
        const matchByNodeId = nodeId === 'xreport-lookup-form' || String(nodeId).includes('xreport-lookup-form');
        const matchByProp = idFromNode === 'xreport-lookup-form' || String(idFromNode).includes('xreport-lookup-form');
        return matchByNodeId || matchByProp;
    };

    const renderSlotContent = (nodeId: string, node: Record<string, unknown>) => {
        if (isLookupFormColumn(nodeId, node)) {
            return <LookupForm reportNo={reportNo} locale={locale} />;
        }
        return null;
    };

    if (isPending) {
        return <LandingSkeleton />;
    }

    if (error) {
        const msg = error instanceof Error ? error.message : String(error);
        console.error('[Landing] Error loading content:', msg);
        return (
            <div id='main-container' role='alert'>
                <p>
                    Landing content is not available. Ensure the report-check API is running at{' '}
                    {getMagnoliaRestBaseURL()}.
                </p>
            </div>
        );
    }

    const areaContent = content?.main;

    return (
        <div id='main-container' style={{ minHeight: 'auto' }}>
            {areaContent && (
                <FragmentRenderer
                    className='gia-fragment-content'
                    content={areaContent as Record<string, unknown>}
                    locale={locale}
                    magnoliaPublicBaseUrl={getMagnoliaRestBaseURL()}
                    renderSlotContent={renderSlotContent}
                />
            )}
        </div>
    );
};

export default Landing;
